import {Component, DestroyRef, EventEmitter, inject, Input, Output, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {OverlayContainer} from '@angular/cdk/overlay';
import {SharedFacadeService} from '@emm/shared/services/shared-facade.service';
import {FormControl} from '@angular/forms';

@Component({
    selector: 'emm-avatar',
    templateUrl: './avatar.component.html',
    styleUrl: './avatar.component.scss',
    standalone: false
})
export class AvatarComponent implements OnInit{
    @Input() fullName = '';
    @Input() isDarkMode = false;

    @Input() initials?: string;
    @Input() backgroundColor?: string;
    @Input() textColor?: string;

    @Output() signOut = new EventEmitter<void>();

    darkModeToggle = new FormControl(false);
    darkModeTooltip = 'This toggles your theme & will persist between sessions.';

    showTagsToggle = new FormControl(true);
    showTagsTooltip = 'This toggle enables displaying tags next to node names in lists & trees. ' +
        'This will persist between sessions.';

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    constructor(private overlay: OverlayContainer, private sharedFacadeService: SharedFacadeService) { }

    ngOnInit() {
        let theme = localStorage.getItem('theme');
        // Default to light-theme if not set
        if (!theme) {
            theme = 'light-theme';
        }
        theme === 'dark-theme' ? this.darkModeToggle.setValue(true) : this.darkModeToggle.setValue(false);
        this.applyTheme(theme);
        this.sharedFacadeService.initTheme();
        this.darkModeToggle.valueChanges
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((darkMode) => {
                darkMode === false ? localStorage.setItem('theme', 'light-theme') :
                    localStorage.setItem('theme', 'dark-theme');
                theme = localStorage.getItem('theme');
                this.applyTheme(theme)
            });

        const showTags = localStorage.getItem('showTags');
        showTags === 'true' ? this.showTagsToggle.setValue(true) : this.showTagsToggle.setValue(false);
        this.showTagsToggle.valueChanges
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((showTagsValue) => {
                const showTagsStr = showTagsValue ? 'true' : 'false';
                localStorage.setItem('showTags', showTagsStr);
                this.sharedFacadeService.selectShowTags(showTagsStr);
            });
    }

    applyTheme(theme: string) {
        localStorage.setItem('theme', theme);
        document.body.classList.remove('light-theme', 'dark-theme');
        document.body.classList.add(theme);
        this.overlay.getContainerElement().classList.remove('light-theme', 'dark-theme');
        this.overlay.getContainerElement().classList.add(theme);
        this.sharedFacadeService.selectTheme(theme);
    }



    get computedInitials(): string {
        if (this.initials?.trim()) return this.initials.trim().toUpperCase();
        const parts = (this.fullName || '').trim().split(/\s+/);
        if (parts.length === 0) return '?';
        const first = parts[0]?.[0] ?? '';
        const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
        return (first + last).toUpperCase();
    }

    onSignOut() {
        this.signOut.emit();
    }
}
